import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* ============================================================
   Theme 00 — Instacut default (shop landing rev-02).

   All three page behaviours from the reference file live here so
   the containing ShopThemed switch stays free of DOM queries:

     1. Top-bar pin guard  (mount-time portal to <body> when a
        broken ancestor chain would defeat position:fixed).
     2. Gallery lightbox   (state-driven native <dialog>).
     3. Consent + dataLayer events (banner + delegated click push).

   Hooks are scoped to this component so themes 01–12 pay no cost
   when a different theme is selected.
   ============================================================ */

const CONSENT_KEY = 'ic_consent_v1';
const CONSENT_MAX_AGE_DAYS = 180;

// ------- shared tiny helpers (kept local; parity with index.js) -------
const initials = (s) =>
  (s || '').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hh = parseInt(h, 10);
  return Number.isNaN(hh) ? t : `${hh}:${m || '00'}`;
};

// ------- consent-mode helpers (module-scoped, no re-creation on render) -------
const gtag = (...args) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

const applyConsent = (granted) => {
  gtag('consent', 'update', {
    ad_storage:         granted ? 'granted' : 'denied',
    ad_user_data:       granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    analytics_storage:  granted ? 'granted' : 'denied',
  });
  window.dataLayer.push({ event: 'consent_choice', granted });
};

const readStoredConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    const age = (Date.now() - v.t) / 86400000;
    return age > CONSENT_MAX_AGE_DAYS ? null : v;
  } catch (e) { console.debug('[shop-theme] localStorage read failed:', e); return null; }
};

const rememberConsent = (granted) => {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ g: granted, t: Date.now() }));
  } catch (e) { console.debug('[shop-theme] localStorage write failed (private mode / quota):', e); }
};

// ============================================================
// TopBar pin guard
// The bar is position:fixed in CSS. That survives ancestor overflow
// (which sticky does not) but is still defeated by any ancestor whose
// computed transform, filter or perspective is not 'none' — those turn
// the ancestor into the containing block, so `fixed` resolves against
// it instead of the viewport. In that case we portal the bar to
// <body> to escape the broken chain, matching instacut-topbar.js.
// ============================================================
const findContainingBlockAncestor = (start) => {
  let el = start && start.parentElement;
  while (el && el !== document.documentElement) {
    const s = getComputedStyle(el);
    if (
      (s.transform    && s.transform    !== 'none') ||
      (s.filter       && s.filter       !== 'none') ||
      (s.perspective  && s.perspective  !== 'none') ||
      /transform|filter|perspective/.test(s.willChange || '')
    ) return el;
    el = el.parentElement;
  }
  return null;
};

const TopBar = ({ shop, initialsChar, onBook, allowPortal, barStatusLabel, barStatusClass, nextSlotHHmm, subnavLinks }) => {
  const localRef = useRef(null);
  const [portalHost, setPortalHost] = useState(null);

  useLayoutEffect(() => {
    // Portaling is opt-in via `allowPortal`. Only the live shop route
    // (/shop/:subdomain) passes it in. Every preview surface — the
    // /profile Live preview (.themed-preview-scaler) and the theme-
    // picker thumbnails (.tcard .scl) both use `transform: scale(...)`
    // to shrink the shop into a phone frame, and either of those would
    // otherwise fool ancestor sniffing into portaling the top bar out
    // to <body>, where it visibly sticks over the admin UI. Opt-in
    // means no future preview wrapper can ever re-introduce that bug.
    if (!allowPortal) return;
    if (portalHost) return;
    const bar = localRef.current;
    if (!bar) return;
    const broken = findContainingBlockAncestor(bar);
    if (broken) {
      console.warn(
        '[instacut] top bar reparented to <body>: ' +
        broken.tagName + '.' + broken.className +
        ' creates a containing block.'
      );
      setPortalHost(document.body);
    }
  }, [allowPortal, portalHost]);

  const bar = (
    <>
      <div className="topbar" data-testid="topbar" ref={localRef}>
        <div className="tb-l">
          {shop.logo_url ? (
            <div
              className="mark logo"
              aria-hidden="true"
              style={{ backgroundImage: `url(${shop.logo_url})` }}
            />
          ) : (
            <div className="mark" aria-hidden="true">{initialsChar}</div>
          )}
          <span className="nm">{shop.name}</span>
        </div>
        <div className="tb-r">
          <div className="tb-live">
            {barStatusLabel && (
              <span
                className={`bar-status ${barStatusClass}${nextSlotHHmm ? '' : ' solo'}`}
                data-testid="shop-status"
              >
                <i aria-hidden="true"></i>{barStatusLabel}
              </span>
            )}
            {nextSlotHHmm && (
              <span className="slot" data-testid="next-slot">
                <span className="k">Next slot</span>
                <b>{nextSlotHHmm}</b>
              </span>
            )}
          </div>
          <button
            className="book"
            type="button"
            onClick={() => onBook()}
            data-testid="topbar-book-btn"
            data-ev="book_click"
            data-ev-loc="topbar"
          >
            Book now
          </button>
        </div>
      </div>
      {subnavLinks && subnavLinks.length > 0 && (
        <nav className="subnav" aria-label="Page sections" data-testid="shop-subnav">
          <ul>
            {subnavLinks.map((l) => (
              <li key={l.id}>
                <a href={`#${l.id}`} data-testid={`subnav-${l.id}`}>{l.label}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  );

  if (portalHost) {
    // Wrap in the theme class so the scoped selectors keep matching
    // once the bar lives outside the natural theme wrapper.
    return createPortal(
      <div className="theme-instacut-default">{bar}</div>,
      portalHost
    );
  }
  return bar;
};

// ============================================================
// Gallery Lightbox — state-driven native <dialog>
// showModal() gives us focus trap, Escape, top-layer stacking and
// focus restore for free; we only own the index.
// ============================================================
const GalleryLightbox = ({ gallery, openIndex, onClose, onNext, onPrev }) => {
  const dlgRef = useRef(null);

  // Sync React state → dialog open/close via imperative API.
  useEffect(() => {
    const d = dlgRef.current;
    if (!d) return;
    if (openIndex >= 0 && !d.open) d.showModal();
    else if (openIndex < 0 && d.open) d.close();
  }, [openIndex]);

  // Arrow keys — only while open. Dialog top-layer holds focus, but
  // registering on window makes the keys work before the dialog gets
  // focus for the first frame.
  useEffect(() => {
    if (openIndex < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); onPrev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, onNext, onPrev]);

  const handleBackdropClick = (e) => {
    // Any click outside the figure and chevrons closes the dialog.
    if (!e.target.closest('.lb-fig') && !e.target.closest('.lb-btn')) {
      dlgRef.current && dlgRef.current.close();
    }
  };

  const current = openIndex >= 0 ? gallery[openIndex] : null;
  const total = gallery.length;

  return (
    <dialog
      className="lb"
      ref={dlgRef}
      onClose={onClose}
      onClick={handleBackdropClick}
      data-testid="gallery-lightbox"
      aria-label="Gallery image viewer"
    >
      <button
        className="lb-btn lb-close"
        type="button"
        aria-label="Close"
        onClick={() => dlgRef.current && dlgRef.current.close()}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
      <button
        className="lb-btn lb-prev"
        type="button"
        aria-label="Previous image"
        onClick={onPrev}
      >
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 5l-7 7 7 7"/></svg>
      </button>
      <figure className="lb-fig">
        {current && (
          <img
            className="lb-img"
            src={current}
            alt=""
          />
        )}
        <figcaption className="lb-cap">
          <span className="t"></span>
          <span className="n">
            {openIndex >= 0 ? `${openIndex + 1} / ${total}` : ''}
          </span>
        </figcaption>
      </figure>
      <button
        className="lb-btn lb-next"
        type="button"
        aria-label="Next image"
        onClick={onNext}
      >
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
      </button>
    </dialog>
  );
};

// ============================================================
// Cookie consent bar
// Deliberately NOT a wall: no backdrop, no scroll lock (CSS already
// takes care of that; the banner only reserves fixed room at the
// bottom). Reject and Accept are the same size and weight so refusal
// is not visually harder — ICO treats an asymmetric banner as
// invalid consent.
// ============================================================
const ConsentBar = ({ show, onDecide }) => (
  <div
    className="consent"
    id="ic-consent"
    role="dialog"
    aria-live="polite"
    aria-label="Cookie choices"
    hidden={!show}
  >
    <p className="c-txt">
      We use cookies to measure how this page performs and how bookings are made.
      {' '}<a href="/privacy">How we use them</a>
    </p>
    <div className="c-act">
      <button
        type="button"
        className="c-btn c-no"
        data-consent="deny"
        onClick={() => onDecide(false)}
      >Reject</button>
      <button
        type="button"
        className="c-btn c-yes"
        data-consent="grant"
        onClick={() => onDecide(true)}
      >Accept</button>
    </div>
  </div>
);

// ============================================================
// The shop landing itself.
// ============================================================
export const InstacutDefaultShop = ({
  shop,
  services,
  barbers,
  hours,
  showHours,
  onBook,
  allowPortal = false,
}) => {
  const slug = 'instacut-default';
  const tagline = shop.tagline?.trim();
  const about   = shop.welcome_text?.trim();
  const gallery = Array.isArray(shop.gallery_urls) ? shop.gallery_urls.filter(Boolean) : [];
  const structuredParts = [shop.address_line_1, shop.address_line_2, shop.city, shop.postcode]
    .map(v => (v || '').trim()).filter(Boolean);
  const structured = structuredParts.length > 0;
  const address = structured ? structuredParts.join(', ') : shop.address?.trim();
  const mapQuery = structured
    ? [shop.city, shop.postcode].filter(v => v && v.trim()).join(', ')
    : address;
  // Map — prefer place_id + Maps Embed API for pinpoint accuracy;
  // fall back to the free q=address embed so the map still shows
  // when the shop hasn't picked its Place ID yet.
  const gmapsKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const rawPlaceId = (shop.google_place_id || '').trim();
  // Guard against malformed values (see themes/index.js for context).
  const placeId = /^[A-Za-z0-9_-]{20,}$/.test(rawPlaceId) ? rawPlaceId : '';
  const mapSrc = (() => {
    if (gmapsKey && placeId) {
      return `https://www.google.com/maps/embed/v1/place?key=${gmapsKey}&q=place_id:${encodeURIComponent(placeId)}`;
    }
    return mapQuery
      ? `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`
      : null;
  })();
  const social  = shop.social_links || {};
  const socialEntries = Object.entries(social).filter(([, v]) => v && String(v).trim());
  const hasHours = (hours || []).some(h => !h.closed);
  // Landing page barber list — show every active barber that has a
  // name. Two photo sources are supported:
  //   • `photo_url`  — a full public URL (legacy / manually set).
  //   • `avatar_url` — a Supabase storage key uploaded via the admin
  //     /barbers page. Served through the backend at
  //     `${BACKEND_URL}/api/barbers/{id}/avatar`.
  // A barber with neither still renders an initials tile so the
  // section never silently disappears when a shop has active
  // barbers but hasn't uploaded photos yet.
  const BACKEND_URL_ENV = process.env.REACT_APP_BACKEND_URL || '';
  const barberPhotoSrc = (b) => {
    if (b.photo_url) return b.photo_url;
    if (b.avatar_url && BACKEND_URL_ENV) return `${BACKEND_URL_ENV}/api/barbers/${b.id}/avatar`;
    return null;
  };
  const presentableBarbers = (barbers || []).filter(b => (b.name || '').trim());

  // Every stored key with a non-empty url renders. Keys are lowercased
  // and trimmed at match time — the raw key is only kept for the
  // capitalised fallback label. `instagram` and `tiktok` get their
  // brand-coloured chips and inline SVG; any other key (facebook,
  // twitter, x, whatsapp…) renders as a `.txt` chip whose CSS
  // `text-transform: capitalize` handles casing.
  const footerSocials = socialEntries;
  const normKey = (k) => String(k).trim().toLowerCase();
  const socialClass = (k) => {
    const key = normKey(k);
    if (key === 'instagram') return 's-ig';
    if (key === 'tiktok')    return 's-tt';
    return 'txt';
  };
  const socialLabel = (k) => {
    const key = normKey(k);
    if (key === 'instagram') return 'Instagram';
    if (key === 'tiktok')    return 'TikTok';
    return key.charAt(0).toUpperCase() + key.slice(1);
  };
  const socialSvg = (k) => {
    const key = normKey(k);
    if (key === 'instagram') return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none"/>
      </svg>
    );
    if (key === 'tiktok') return (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
        <path d="M16.5 3h-3v11.1c0 1.1-.8 2-1.9 2.1a2 2 0 1 1-.5-4c.2 0 .5 0 .7.1V9.5c-1.8-.3-3.6.3-4.8 1.7-1.8 2.1-1.4 5.4.8 7 3.4 2.4 8 .1 8-4.2V8.3c1.2.8 2.5 1.2 3.8 1.1V6.8C18.1 6.6 16.8 5.2 16.5 3Z"/>
      </svg>
    );
    return null;
  };

  const svcIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3"/>
      <path d="M8.12 8.12 12 12"/>
      <path d="M20 4 8.12 15.88"/>
      <circle cx="6" cy="18" r="3"/>
      <path d="M14.8 14.8 20 20"/>
    </svg>
  );

  // London day for the row.today marker. Must use SHORT weekday
  // ('Wed') because the `hours` prop rows coming from ShopHome use
  // DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']. Long
  // format ('Wednesday') never matched, silently killing the Today
  // marker AND the Opening-soon 'opens HH:MM' suffix (iteration_56).
  const londonDay = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', timeZone: 'Europe/London',
  }).format(new Date());

  // ---- live-status fetch (page-load only, not real-time) ----
  // One HTTP round-trip on mount hydrates the four labels that used
  // to be static placeholder text: .bar-status, .slot, .hr-status and
  // (via shop.deposit_*) .svc-note. Response shape:
  //   { status, closes_at, next_slot }
  const [live, setLive] = useState({
    status: null,        // 'open_now'|'opening_soon'|'closing_soon'|'closed'
    closes_at: null,     // 'HH:MM' when the shop is open, else null
    next_slot: null,     // ISO string with Europe/London offset, or null
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${BACKEND_URL}/api/shops/${shop.id}/live-status`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setLive(data);
      } catch (e) {
        // Non-fatal — labels fall back to hidden / neutral text.
      }
    })();
    return () => { cancelled = true; };
  }, [shop.id]);

  // -------- Derived UI strings for the four wired spots --------
  // Central so bar-status / hr-status stay in sync. Before /live-status
  // resolves (or when it errors) we render nothing rather than an
  // optimistic 'Open now' — a stale green pill on a closed shop is a
  // trust bug.
  const STATUS_LABEL = {
    open_now:      'Open now',
    opening_soon:  'Opening soon',
    closing_soon:  'Closing soon',
    closed:        'Closed',
  };
  const STATUS_CLASS = {
    open_now:      'open',
    opening_soon:  'soon',
    closing_soon:  'soon',
    closed:        'shut',
  };
  const barStatusLabel = live.status ? STATUS_LABEL[live.status] : null;
  const barStatusIsOpen = live.status === 'open_now';
  const barStatusClass = live.status ? STATUS_CLASS[live.status] : '';

  // Hours-pill copy mirrors the bar. When we know the closing time
  // append `· closes HH:MM`; for opening_soon append `· opens HH:MM`
  // from today's opening_hours row.
  const todayOpeningRow = React.useMemo(() => {
    // hours prop from ShopHome uses the theme shape { label, closed,
    // start, end } in Mon..Sun order; match today's label to pull
    // start.
    return (hours || []).find(h => h.label === londonDay) || null;
  }, [hours, londonDay]);
  const hrStatusText = (() => {
    if (!live.status) return null;
    if (live.status === 'closed') return 'Closed today';
    if (live.status === 'opening_soon' && todayOpeningRow?.start) {
      return { label: 'Opening soon', suffix: `opens ${todayOpeningRow.start.slice(0,5)}` };
    }
    const label = STATUS_LABEL[live.status];
    if (live.closes_at) return { label, suffix: `closes ${live.closes_at}` };
    return { label, suffix: null };
  })();

  // Format the next slot as HH:mm Europe/London, 24h.
  const nextSlotHHmm = live.next_slot
    ? new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London',
      }).format(new Date(live.next_slot))
    : null;

  // ---- deposit note (svc-note): read from shop.deposit_* ----
  const depositNote = (() => {
    if (!shop.deposit_required) return null;
    const amt = Number(shop.deposit_amount);
    if (!amt || Number.isNaN(amt)) return null;
    const asPercent = shop.deposit_type === 'percent';
    return asPercent
      ? { label: `${amt}% deposit` }
      : { label: `£${amt.toFixed(0)} deposit` };
  })();

  // ---- Reviews: Google-proxy OR Instacut own list ----
  // TODO: post-booking review email/SMS trigger to be built separately
  //       once the booking-completed hook lands. That will POST new
  //       Instacut reviews via /shops/{id}/reviews (not built yet).
  const useGoogle = shop.reviews_source === 'google' && !!shop.google_place_id;
  const [googleReviews, setGoogleReviews] = useState(null);   // {rating,total,url,reviews[]}
  useEffect(() => {
    if (!useGoogle) return;
    let cancelled = false;
    (async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${BACKEND_URL}/api/shops/${shop.id}/google-reviews`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setGoogleReviews(data);
      } catch (e) { console.debug('[shop-theme] reviews section hidden — fetch failed:', e); }
    })();
    return () => { cancelled = true; };
  }, [useGoogle, shop.id]);

  // Normalise to the .q markup input regardless of source.
  const visibleReviews = useGoogle
    ? (googleReviews?.reviews || [])
    : (Array.isArray(shop.reviews) ? shop.reviews : [])
        .filter(r => r && !r.hidden && (r.review || r.quote))
        .slice(0, 3)
        .map((r, i) => ({
          id: r.id || `r-${i}`,
          name: r.name || r.author || 'Anonymous',
          review: r.review || r.quote || '',
          rating: Number(r.rating ?? 5),
          when: '',
        }));
  const reviewsHeadRating = useGoogle ? (googleReviews?.rating ?? null) : null;
  const reviewsHeadTotal  = useGoogle ? (googleReviews?.total  ?? 0)    : null;
  const reviewsAllUrl     = useGoogle ? (googleReviews?.url    || null) : null;

  // -------- lightbox state --------
  const [lbIndex, setLbIndex] = useState(-1);
  const lbNext  = () => setLbIndex(i => (i + 1) % gallery.length);
  const lbPrev  = () => setLbIndex(i => (i - 1 + gallery.length) % gallery.length);
  const lbClose = () => setLbIndex(-1);

  // -------- consent banner state --------
  const [showConsent, setShowConsent] = useState(false);
  useEffect(() => {
    const prior = readStoredConsent();
    if (prior) applyConsent(prior.g);
    else setShowConsent(true);
  }, []);
  const chooseConsent = (granted) => {
    applyConsent(granted);
    rememberConsent(granted);
    setShowConsent(false);
  };

  // -------- data-ev / gallery_open / review_outbound dispatcher --------
  // A single delegated listener on document catches events both from the
  // in-tree markup and from the portaled top bar (which may live under
  // <body>). Consent Mode gates what tags DO with the events, not
  // whether the push happens — per the reference file.
  useEffect(() => {
    const onClick = (e) => {
      window.dataLayer = window.dataLayer || [];
      const evEl = e.target.closest && e.target.closest('[data-ev]');
      if (evEl) {
        window.dataLayer.push({
          event:    evEl.dataset.ev,
          location: evEl.dataset.evLoc || '',
          shop:     shop.subdomain || shop.slug || '',
        });
      }
      if (e.target.closest && e.target.closest('.theme-instacut-default .gallery .grid .ph')) {
        window.dataLayer.push({ event: 'gallery_open' });
      }
      if (e.target.closest && e.target.closest('.theme-instacut-default .q-src, .theme-instacut-default .rv-all')) {
        window.dataLayer.push({ event: 'review_outbound' });
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [shop.subdomain, shop.slug]);

  const initialsChar = initials(shop.name).charAt(0);

  // Sub-nav: one link per section that will actually render. Order
  // matches the visual sequence down the page. `services` is always
  // present (it has its own empty-state), so it's unconditional.
  const subnavLinks = [
    about                          && { id: 'sec-about',    label: 'About'    },
                                      { id: 'sec-services', label: 'Services' },
    presentableBarbers.length > 0  && { id: 'sec-barbers',  label: 'Barbers'  },
    showHours                      && { id: 'sec-hours',    label: 'Hours'    },
    gallery.length > 0             && { id: 'sec-gallery',  label: 'Gallery'  },
    mapSrc                         && { id: 'sec-location', label: 'Location' },
  ].filter(Boolean);

  return (
    <div className={`theme-${slug}`} data-testid="shop-home" data-theme={slug}>

      <TopBar
        shop={shop}
        initialsChar={initialsChar}
        onBook={onBook}
        allowPortal={allowPortal}
        barStatusLabel={barStatusLabel}
        barStatusClass={barStatusClass}
        nextSlotHHmm={nextSlotHHmm}
        subnavLinks={subnavLinks}
      />

      <div className="shell">

        {/* ============ HERO ============ */}
        {/* When the shop has uploaded a hero image we tack on `has-bg`
            which flips the section into the "photo hero" layout (see
            themes.css: min-height, bottom-anchored h1, dark scrim
            ::before). The image itself is inlined so `hero_opacity`
            (0.1..1.0) can fade it via a white overlay layer without
            touching the scrim used for text legibility. */}
        {(() => {
          const heroUrl = shop.hero_image_url;
          const heroOp  = Math.max(0.1, Math.min(1.0,
            typeof shop.hero_opacity === 'number' ? shop.hero_opacity : 1.0));
          const heroStyle = heroUrl ? {
            backgroundImage: `linear-gradient(rgba(255,255,255,${1 - heroOp}), rgba(255,255,255,${1 - heroOp})), url(${heroUrl})`,
          } : undefined;
          return (
            <section className={`hero${heroUrl ? ' has-bg' : ''}`} style={heroStyle}>
              <h1 data-testid="hero-name">{shop.name}</h1>
              {tagline && <p data-testid="hero-tagline">{tagline}</p>}
            </section>
          );
        })()}

        {/* ============ ABOUT ============ */}
        {about && (
          <section id="sec-about" className="sec about" data-testid="section-about">
            <h2 className="sec-h">About</h2>
            <p>{about}</p>
          </section>
        )}

        {/* ============ SERVICES ============ */}
        <section id="sec-services" className="sec services" data-testid="section-services">
          <h2 className="sec-h">Services</h2>
          {services.length === 0 ? (
            <p style={{ opacity: 0.6, fontSize: 14 }}>No services available yet.</p>
          ) : (
            <div className="svc-list" data-testid="svc-list">
              {services.map((s, idx) => (
                <button
                  key={s.id}
                  className="svc"
                  type="button"
                  onClick={() => onBook(s.id)}
                  data-testid={`service-${s.id}`}
                  data-ev="service_click"
                  data-ev-loc={`service-${s.id}`}
                >
                  <span className="num">{String(idx + 1).padStart(2, '0')}</span>
                  <span className="icon" aria-hidden="true">{svcIcon}</span>
                  <span className="l">
                    <b>{s.name}</b>
                    <small>{s.duration_min} min{s.description ? ` · ${s.description}` : ''}</small>
                  </span>
                  <span className="p">£{Number(s.price).toFixed(0)}</span>
                </button>
              ))}
            </div>
          )}
          {/* svc-note — driven by shop.deposit_required / deposit_amount /
              deposit_type from ShopProfile. Hidden entirely when the shop
              hasn't turned deposits on. */}
          {depositNote && (
            <p className="svc-note" data-testid="deposit-note">
              <svg className="i" viewBox="0 0 24 24" width="17" height="17" fill="none"
                   stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                   strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 7.6h.01"/>
              </svg>
              <span>A <b>{depositNote.label}</b> is taken when you book and comes off the price of your cut.
              Cancel free up to <b>24 hours</b> before your appointment.</span>
            </p>
          )}
        </section>

        {/* ============ BARBERS (collapsible) ============ */}
        {presentableBarbers.length > 0 && (
          <section id="sec-barbers" className="sec barbers" data-testid="section-barbers">
            <details className="fold" open>
              <summary className="sec-h"><h2>Barbers</h2></summary>
              <div className="list">
                {presentableBarbers.map(b => {
                  const src = barberPhotoSrc(b);
                  return (
                    <div className="b" key={b.id} data-testid={`barber-${b.id}`}>
                      <div
                        className="ph"
                        style={src ? { backgroundImage: `url(${src})` } : {}}
                        aria-hidden="true"
                      >
                        {!src && initials(b.name).charAt(0)}
                      </div>
                      <div className="nm">
                        <h3>{b.name}</h3>
                        {b.role && <small>{b.role}</small>}
                        {b.bio && <p>{b.bio}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          </section>
        )}

        {/* ============ HOURS (collapsible) ============ */}
        {showHours && (
          <section id="sec-hours" className="sec hours" data-testid="section-hours">
            <details className="fold" open>
              <summary className="sec-h">
                <h2>Hours</h2>
                {hrStatusText && (
                  typeof hrStatusText === 'string' ? (
                    <span
                      className={`hr-status ${barStatusClass}`}
                      data-testid="hours-status"
                    >{hrStatusText}</span>
                  ) : (
                    <span
                      className={`hr-status ${barStatusClass}`}
                      data-testid="hours-status"
                    >
                      {hrStatusText.label}
                      {hrStatusText.suffix && <><i>·</i>{hrStatusText.suffix}</>}
                    </span>
                  )
                )}
              </summary>
              {hasHours ? (
                <div className="hr-panel">
                  {(hours || []).map(h => {
                    const isToday = h.label === londonDay;
                    const isClosed = !!h.closed;
                    const cls = ['row', isToday ? 'is-today' : '', isClosed ? 'closed' : '']
                      .filter(Boolean).join(' ');
                    return (
                      <div className={cls} key={h.label}>
                        <span className="d">
                          {h.label}
                          {isToday && <span className="tag">Today</span>}
                        </span>
                        <span className="h">
                          {isClosed
                            ? 'Closed'
                            : <>{formatTime(h.start)}<i>–</i>{formatTime(h.end)}</>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ opacity: 0.6, fontSize: 14 }}>Hours not set yet.</p>
              )}
            </details>
          </section>
        )}

        {/* ============ GALLERY (collapsible + lightbox) ============ */}
        {gallery.length > 0 && (
          <section id="sec-gallery" className="sec gallery" data-testid="section-gallery">
            <details className="fold" open>
              <summary className="sec-h"><h2>Gallery</h2></summary>
              <div className="grid">
                {gallery.map((url, i) => (
                  <button
                    key={url}
                    className="ph"
                    type="button"
                    data-full={url}
                    data-cap=""
                    aria-label={`Open image ${i + 1} of ${gallery.length}`}
                    onClick={() => setLbIndex(i)}
                  >
                    <img
                      src={url}
                      width="320"
                      height="320"
                      loading="lazy"
                      decoding="async"
                      alt={shop.name}
                    />
                  </button>
                ))}
              </div>
            </details>

            <GalleryLightbox
              gallery={gallery}
              openIndex={lbIndex}
              onClose={lbClose}
              onNext={lbNext}
              onPrev={lbPrev}
            />
          </section>
        )}

        {/* ============ REVIEWS (collapsible) — real data ============ */}
        {visibleReviews.length > 0 && (
        <section className="sec reviews" data-testid="section-reviews">
          <details className="fold" open>
            <summary className="sec-h"><h2>Reviews</h2></summary>

            <div className="rv-panel">
              {useGoogle && reviewsHeadRating != null && (
                <div className="rv-head">
                  <div className="rv-score">
                    <b>{Number(reviewsHeadRating).toFixed(1)}</b>
                    <span className="stars" style={{ '--rate': reviewsHeadRating }} role="img" aria-label={`Rated ${reviewsHeadRating} out of 5`}></span>
                  </div>
                  <div className="rv-meta">
                    <span className="rv-count">{reviewsHeadTotal} Google reviews</span>
                    <span className="rv-sort">Sorted by most relevant</span>
                  </div>
                  {reviewsAllUrl && (
                    <a className="rv-all" href={reviewsAllUrl} target="_blank" rel="noopener noreferrer" data-ev="review_outbound" data-ev-loc="rv-all">All reviews</a>
                  )}
                </div>
              )}

              {visibleReviews.map((r, i) => (
                <div className="q" key={r.id} data-testid={`review-${i}`}>
                  <div className="q-top">
                    <span className="av" aria-hidden="true">{(r.name || '?').charAt(0).toUpperCase()}</span>
                    <span className="who"><b>{r.name}</b>{r.when && <span className="when">{r.when}</span>}</span>
                    <span className="stars sm" style={{ '--rate': r.rating }} role="img" aria-label={`Rated ${r.rating} out of 5`}></span>
                  </div>
                  <blockquote>{r.review}</blockquote>
                  {useGoogle && reviewsAllUrl && (
                    <a className="q-src" href={reviewsAllUrl} target="_blank" rel="noopener noreferrer" data-ev="review_outbound" data-ev-loc={`review-${i}`}>View on Google</a>
                  )}
                </div>
              ))}

              {useGoogle && (
                <div className="rv-attr">
                  <span className="g-logo" data-slot="google-logo">Google</span>
                </div>
              )}
            </div>
          </details>
        </section>
        )}

        {/* ============ LOCATION ============ */}
        {mapSrc && (
          <section id="sec-location" className="sec location" data-testid="section-location">
            <h2 className="sec-h">Location</h2>
            <iframe
              className="map"
              title="Map"
              src={mapSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </section>
        )}

        {/* ============ FOOTER — contact strip ============ */}
        <section className="foot" data-testid="section-footer">
          <div className="ft-grid">

            <div className="ft-col" data-testid="footer-contact">
              <div className="ft-h">Call the shop</div>
              {shop.phone && (
                <a
                  className="ft-tel"
                  href={`tel:${(shop.phone || '').replace(/\s/g, '')}`}
                  data-ev="call_click"
                  data-ev-loc="footer"
                >{shop.phone}</a>
              )}
              {shop.email && (
                <a
                  className="ft-mail"
                  href={`mailto:${shop.email}`}
                >{shop.email}</a>
              )}
            </div>

            <div className="ft-col">
              <div className="ft-h">Find us</div>
              {address && (
                <address className="ft-addr">
                  {address.split(/\r?\n|,\s*/).filter(Boolean).map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}{i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </address>
              )}
              {footerSocials.length > 0 && (
                <div className="social" data-testid="footer-social">
                  {footerSocials.map(([k, v]) => (
                    <a
                      key={k}
                      className={socialClass(k)}
                      href={String(v)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-ev="social_click"
                      data-ev-loc={String(k).toLowerCase()}
                      aria-label={`${shop.name} on ${socialLabel(k)}`}
                    >
                      {socialSvg(k)}
                      <span className="lbl">{socialLabel(k)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

          </div>

          <div className="fine">
            <div className="ft-legal">
              <span className="cr">© {new Date().getFullYear()} {shop.name}</span>
              <span className="ft-links">
                <a href="/terms">Terms</a>
                <a href="/privacy">Privacy</a>
              </span>
            </div>
            <a
              className="ic-by"
              href="https://instacut.co.uk/?ref=shopfront"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Powered by Instacut — booking software for barbershops"
            >
              <span className="by">Powered by</span> <b>Instacut</b>
            </a>
          </div>
        </section>

      </div>

      <ConsentBar show={showConsent} onDecide={chooseConsent} />

    </div>
  );
};

export default InstacutDefaultShop;
